import { Injectable } from '@angular/core';
import {
  ReportSectionConfigForDraggableItem,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationWithOrder,
  ReportSectionDataSection,
  ReportSectionNestedDataSection,
  ReportSectionPlacementType,
  TemplateDesignerSelectedReportState
} from '@capital-access/reporting/common';
import {
  ActivityHistoryConfiguration,
  ActivityIndicatorsConfiguration,
  CombinedOwnershipConfiguration,
  DisclosureConfiguration,
  DRPriceVolumeConfiguration,
  EquityPortfolioConfiguration,
  FundsSummaryConfiguration,
  HistoricalOwnershipConfiguration,
  MonthlyDrActivityConfiguration,
  PeerAndIndustryOwnershipConfiguration,
  SustainabilityFitMetricConfiguration,
  TargetingConfiguration,
  YTDPriceVolumeConfiguration
} from '@capital-access/reporting/report-section';
import { getReportSectionConfig } from '../utils/template-designer.utils';

@Injectable()
export class ItineraryParticipantInfoConfigurationPanelService {
  public static ConvertReportSectionsToDraggableList(
    sections: ReportSectionConfigurationWithOrder[]
  ): ReportSectionConfigForDraggableItem[] {
    return sections.map(section => {
      const updatedConfig = section.config;
      const adjustedWidth = section.width
        ? Math.min(Math.max(section.width, updatedConfig.minSize), updatedConfig.maxSize)
        : section.width;

      // Calculate total and selected datapoints counts for this section
      let totalDataPoints = 0;
      let selectedDataPoints = 0;

      if (updatedConfig.availableDataSections && Array.isArray(updatedConfig.availableDataSections)) {
        updatedConfig.availableDataSections.forEach(item => {
          if (item.subSections && Array.isArray(item.subSections) && item.subSections.length > 0) {
            // Parent item with subsections - count subsections
            totalDataPoints += item.subSections.length;
            selectedDataPoints += item.subSections.filter(sub => sub.isSelected).length;
          } else {
            // No subsections - count the parent item
            totalDataPoints += 1;
            if (item.isSelected) {
              selectedDataPoints += 1;
            }
          }
        });
      }

      return {
        order: section.order,
        name: updatedConfig.name,
        minSize: updatedConfig.minSize,
        maxSize: updatedConfig.maxSize,
        disabledLayoutWidthControl: updatedConfig?.disabledLayoutWidthControl,
        disableSectionSelection: updatedConfig?.disableSectionSelection,
        disableSectionReordering: updatedConfig?.disableSectionReordering,
        displayName: updatedConfig?.displayName || '',
        groupName: updatedConfig?.groupName,
        totalDataSectionsCount: totalDataPoints || updatedConfig?.totalDataSectionsCount,
        selectedDataSectionsCount: selectedDataPoints || updatedConfig?.selectedDataSectionsCount,
        width: adjustedWidth,
        checkBoxState: section.isSelected,
        isParentItem: true,
        canSelectAllDataSections: updatedConfig.canSelectAllDataSections,
        maxGridUnitsAllowed: updatedConfig.maxGridUnitsAllowed,
        noCommentsReordering: updatedConfig.name === 'enableItineraryCommentary',
        noActivityHeaderReordering: updatedConfig.name === 'activityHeader',
        noActivityAttributesReordering: updatedConfig.name === 'activityAttributes',
        noParticipantInfoReordering: updatedConfig.name === 'participantsInfo',
        activityHeaderTurnOff: updatedConfig.name === 'activityHeader',
        participantInfoTurnOff: updatedConfig.name === 'participantsInfo',
        noDataPointsAvailable:
          updatedConfig.name === 'enableItineraryCommentary' ||
          updatedConfig.name === 'enableCommentary' ||
          (totalDataPoints === 0 && updatedConfig.totalDataSectionsCount === 0),
        disabled: updatedConfig?.placementType === ReportSectionPlacementType.Static,
        isStatic: updatedConfig?.placementType === ReportSectionPlacementType.Static,
        nestedItems: updatedConfig?.nestedItems
          ? [...this.ConvertNestedItemsToDraggableList(updatedConfig.nestedItems)]
          : [],
        totalColumnsToDisplay: updatedConfig?.totalColumnsToDisplay
      } as ReportSectionConfigForDraggableItem;
    });
  }

  public static ConvertNestedItemsToDraggableList(
    nestedItems: ReportSectionNestedDataSection[]
  ): ReportSectionConfigForDraggableItem[] {
    return (nestedItems ?? []).map((item: ReportSectionNestedDataSection) => {
      const config = item.nestedItemSectionConfiguration?.config;
      const adjustedNestedWidth = item.nestedItemSectionConfiguration?.width
        ? Math.min(Math.max(item.nestedItemSectionConfiguration?.width, config?.minSize ?? 0), config?.maxSize ?? 0)
        : item.nestedItemSectionConfiguration?.width;

      return {
        name: item.name,
        displayName: config?.displayName || '',
        groupName: config?.groupName,
        checkBoxState: item.nestedItemSectionConfiguration?.isSelected,
        order: item.nestedItemSectionConfiguration?.order,
        minSize: config?.minSize,
        maxSize: config?.maxSize,
        width: adjustedNestedWidth,
        isParentItem: false,
        disabled: config?.placementType === ReportSectionPlacementType.Static,
        isStatic: config?.placementType === ReportSectionPlacementType.Static,
        totalDataSectionsCount: config?.totalDataSectionsCount,
        selectedDataSectionsCount: config?.selectedDataSectionsCount,
        totalColumnsToDisplay: config?.totalColumnsToDisplay
      } as ReportSectionConfigForDraggableItem;
    }) as ReportSectionConfigForDraggableItem[];
  }

  public static IsReportSectionConfigWidthValid(
    width: number,
    reportSection: ReportSectionConfigurationWithOrder
  ): boolean {
    return width >= reportSection.config.minSize && width <= reportSection.config.maxSize;
  }

  public static GetWidthErrorMessage(section: ReportSectionConfigurationWithOrder): string {
    return `Width must be between ${section.config.minSize} and ${section.config.maxSize}`;
  }

  public static ReportSectionCheckboxToggle(
    checked: boolean,
    reportSections: ReportSectionConfigurationWithOrder[],
    reportSectionName: string,
    checkAll = false
  ): ReportSectionConfigurationWithOrder[] {
    return reportSections.map(section => {
      // Check if the target is a nested item
      const isSectionContainsNestedItems =
        Array.isArray(section.config.nestedItems) && section.config.nestedItems.length !== 0;

      // Update nested items selection if applicable
      const updatedNestedItems = isSectionContainsNestedItems
        ? section.config.nestedItems?.map(nested => {
            if (!reportSectionName || nested.name === reportSectionName) {
              return {
                ...nested,
                nestedItemSectionConfiguration: {
                  ...nested.nestedItemSectionConfiguration,
                  isSelected: nested.nestedItemSectionConfiguration?.config?.disableSectionSelection
                    ? nested.nestedItemSectionConfiguration?.isSelected
                    : checked
                }
              };
            }
            return nested;
          })
        : section.config.nestedItems;

      // Update parent section selection if applicable
      const hasTargetNested = section.config.nestedItems?.some(n => n.name === reportSectionName);
      if (checkAll || section.config.name === reportSectionName || hasTargetNested) {
        return {
          ...section,
          isSelected: section.config.disableSectionSelection ? section.isSelected : checked,
          config: {
            ...section.config,
            nestedItems: updatedNestedItems
          }
        } as ReportSectionConfigurationWithOrder;
      }
      return section;
    });
  }

  public static FindSelectedReportSectionConfig(
    reportSections: ReportSectionConfigurationWithOrder[],
    selectedReport: ReportSectionConfigForDraggableItem
  ): TemplateDesignerSelectedReportState | null {
    const reportSectionConfig = getReportSectionConfig(reportSections, selectedReport?.name);
    return getTemplateDesignerSelectedReportState(reportSectionConfig);
  }

  public static PrepareDraggableItemsConfig(
    sectionConfig: ReportSectionConfigurationWithOrder
  ): Partial<ReportSectionConfigForDraggableItem>[] {
    const nestedItems = sectionConfig.config.availableDataSections.map((section: ReportSectionDataSection) => {
      const subSections =
        section.subSections?.map(subSection => {
          return {
            name: subSection.name,
            displayName: subSection.displayName,
            groupName: subSection.groupName,
            disabled: !section.canReorderSubSections,
            checkBoxState: subSection.isSelected,
            isParentItem: false,
            iconClass: subSection.icon,
            unit: subSection.unit
          } as Partial<ReportSectionConfigForDraggableItem>;
        }) || [];
      return {
        name: section.name,
        displayName: section.displayName,
        groupName: section.groupName,
        checkBoxState: section.isSelected,
        isParentItem: false,
        canReorderSubSections: section.canReorderSubSections || false,
        nestedItems: subSections,
        iconClass: section.icon,
        unit: section.unit
      } as ReportSectionConfigForDraggableItem;
    });

    // Calculate total and selected datapoints counts
    let totalDataPoints = 0;
    let selectedDataPoints = 0;

    nestedItems.forEach(item => {
      if (item.nestedItems && Array.isArray(item.nestedItems) && item.nestedItems.length > 0) {
        // Parent item with subsections - count subsections
        totalDataPoints += item.nestedItems.length;
        selectedDataPoints += item.nestedItems.filter(sub => sub.checkBoxState).length;
      } else {
        // No subsections - count the parent item
        totalDataPoints += 1;
        if (item.checkBoxState) {
          selectedDataPoints += 1;
        }
      }
    });

    const draggableItem = {
      name: sectionConfig?.config.name,
      isStatic: true,
      checkBoxState: sectionConfig.isSelected,
      order: sectionConfig.order,
      displayName: sectionConfig.config.displayName,
      isParentItem: true,
      width: sectionConfig.width,
      reportSection: sectionConfig.config.reportSection,
      minSize: sectionConfig.config.minSize,
      maxSize: sectionConfig.config.maxSize,
      totalDataSectionsCount: totalDataPoints,
      selectedDataSectionsCount: selectedDataPoints,
      canSelectAllDataSections: sectionConfig.config.canSelectAllDataSections,
      maxGridUnitsAllowed: sectionConfig.config.maxGridUnitsAllowed,
      totalColumnsToDisplay: sectionConfig.config.totalColumnsToDisplay,
      nestedItems: nestedItems
    };
    return [draggableItem];
  }

  public static UpdateReportSectionOrder(
    draggableItems: ReportSectionConfigForDraggableItem[],
    reportSections: ReportSectionConfigurationWithOrder[]
  ): ReportSectionConfigurationWithOrder[] {
    const orderMap = new Map(draggableItems.map((item, index) => [item.name, index]));

    const nestedItemOrderMap = new Map<string, number>();
    draggableItems.forEach(item => {
      if (item.nestedItems) {
        item.nestedItems.forEach((nestedItem, index) => {
          nestedItemOrderMap.set(nestedItem.name, index);
        });
      }
    });

    // subsectionOrderMap uses 1-based indexing (index + 1) for subsection orders (different purpose than nestedItemOrderMap)
    // nestedItemOrderMap uses 0-based indexing for nested item orders
    const subsectionOrderMap = new Map<string, number>(
      draggableItems.flatMap(item => item.nestedItems?.map((nestedItem, index) => [nestedItem.name, index + 1]) ?? [])
    );

    const hasSubsections = subsectionOrderMap.size > 0;

    const getUpdatedSubSections = (subSection: ReportSectionDataSection): ReportSectionDataSection[] | undefined => {
      if (!hasSubsections || !subSection.subSections?.length) {
        return subSection.subSections;
      }

      return subSection.subSections
        .map((subSec: ReportSectionDataSection) => {
          const order = subsectionOrderMap.get(subSec.name);
          const deeperSubSections = subSec.subSections ? getUpdatedSubSections(subSec) : undefined;

          return {
            ...subSec,
            order: order ?? subSec.order,
            subSections: deeperSubSections
          };
        })
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    };

    const getUpdatedAvailableDataSections = (
      section: ReportSectionConfigurationWithOrder
    ): ReportSectionDataSection[] => {
      if (section.config?.disableSectionReordering !== true || !section.config.availableDataSections) {
        return section.config.availableDataSections;
      }

      return section.config.availableDataSections
        .map(subSection => {
          const order = orderMap.get(subSection.name);
          const subSections = getUpdatedSubSections(subSection);

          return {
            ...subSection,
            order: order ?? subSection.order,
            subSections
          };
        })
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    };

    const getUpdatedNestedItems = (
      section: ReportSectionConfigurationWithOrder
    ): ReportSectionNestedDataSection[] | undefined => {
      if (section.config.name !== 'participantsInfo' || !section.config.nestedItems) {
        return section.config.nestedItems;
      }

      return section.config.nestedItems
        .map(nestedItem => {
          const newOrder = nestedItemOrderMap.get(nestedItem.name);
          if (newOrder !== undefined && nestedItem.nestedItemSectionConfiguration) {
            return {
              ...nestedItem,
              nestedItemSectionConfiguration: {
                ...nestedItem.nestedItemSectionConfiguration,
                order: newOrder
              }
            };
          }
          return nestedItem;
        })
        .sort((a, b) => {
          const orderA = a.nestedItemSectionConfiguration?.order ?? 999;
          const orderB = b.nestedItemSectionConfiguration?.order ?? 999;
          return orderA - orderB;
        });
    };

    return reportSections
      .map(section => {
        const order = orderMap.get(section.config.name);
        const availableDataSections = getUpdatedAvailableDataSections(section);
        const nestedItems = getUpdatedNestedItems(section);

        return {
          ...section,
          order: order ?? section.order,
          config: {
            ...section.config,
            availableDataSections,
            nestedItems: nestedItems ?? section.config.nestedItems
          }
        } as ReportSectionConfigurationWithOrder;
      })
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  public static ReportSectionDataPointSelectClearAll(
    checked: boolean,
    reportSection: ReportSectionConfigurationWithOrder
  ): ReportSectionConfigurationWithOrder {
    const updatedAvailableDataSections = reportSection.config.availableDataSections.map(section => ({
      ...section,
      isSelected: section.canConfigure && !section.isStatic ? checked : section.isSelected,
      subSections: section.subSections?.map(subSection => ({
        ...subSection,
        isSelected: subSection.canConfigure && !subSection.isStatic ? checked : subSection.isSelected
      }))
    }));

    const { selectedCount, staticCount } = updatedAvailableDataSections.reduce(
      (acc, section) => {
        if (section.isSelected) acc.selectedCount++;
        if (section.isStatic) acc.staticCount++;

        section.subSections?.forEach(sub => {
          if (sub.isSelected) acc.selectedCount++;
          if (sub.isStatic) acc.staticCount++;
        });
        return acc;
      },
      { selectedCount: 0, staticCount: 0 }
    );

    const updatedConfig = {
      ...reportSection.config,
      availableDataSections: updatedAvailableDataSections,
      selectedDataSectionsCount:
        staticCount > 0 && staticCount === selectedCount ? selectedCount : checked ? selectedCount : 0
    };

    const updatedReportsection = {
      ...reportSection,
      isSelected: reportSection?.config.disableSectionSelection
        ? reportSection.isSelected
        : checked && selectedCount > 0,
      config: updatedConfig as ReportSectionConfigurationBase
    };

    const { maxSize, minSize } =
      ItineraryParticipantInfoConfigurationPanelService.GetUpdatedSizes(updatedReportsection);
    updatedReportsection.config.minSize = minSize;
    updatedReportsection.config.maxSize = maxSize;

    return updatedReportsection;
  }

  private static CalculateTotalSelectedUnits(sections: ReportSectionDataSection[]): number {
    return sections.reduce((acc, section: ReportSectionDataSection) => {
      const sectionUnits = section.isSelected ? section.unit || 0 : 0;
      const subSectionUnits =
        section.subSections?.filter(sub => sub.isSelected).reduce((subAcc, sub) => subAcc + (sub.unit || 0), 0) || 0;
      return acc + sectionUnits + subSectionUnits;
    }, 0);
  }

  private static CanUpdateDataSectionUnit(
    targetSection: ReportSectionDataSection,
    reportSection: ReportSectionConfigurationWithOrder,
    maxGridUnitsAllowed: number,
    checked: boolean
  ): boolean {
    const totalSelectedUnits = ItineraryParticipantInfoConfigurationPanelService.CalculateTotalSelectedUnits(
      reportSection.config.availableDataSections
    );
    const targetUnits = targetSection?.unit || 0;

    if (checked) {
      return totalSelectedUnits + targetUnits <= maxGridUnitsAllowed;
    }
    return totalSelectedUnits - targetUnits > 0;
  }

  //  add list of config classes that needs minSize property change on data section toggle
  private static getConfigurationInstanceFromConfigClass(
    reportSection: ReportSectionConfigurationWithOrder
  ): ReportSectionConfigurationBase | undefined {
    const configClass = reportSection.config.typeName;
    switch (configClass) {
      case 'FundsSummaryConfiguration':
        return new FundsSummaryConfiguration();
      case 'EquityPortfolioConfiguration':
        return new EquityPortfolioConfiguration();
      case 'TargetingConfiguration':
        return new TargetingConfiguration();
      case 'CombinedOwnershipConfiguration':
        return new CombinedOwnershipConfiguration();
      case 'PeerAndIndustryOwnershipConfiguration':
        return new PeerAndIndustryOwnershipConfiguration();
      case 'HistoricalOwnershipConfiguration':
        return new HistoricalOwnershipConfiguration();
      case 'ActivityHistoryConfiguration':
        return new ActivityHistoryConfiguration();
      case 'SustainabilityFitMetricConfiguration':
        return new SustainabilityFitMetricConfiguration();
      case 'ActivityIndicatorsConfiguration':
        return new ActivityIndicatorsConfiguration();
      case 'MonthlyDrActivityConfiguration':
        return new MonthlyDrActivityConfiguration();
      case 'DRPriceVolumeConfiguration':
        return new DRPriceVolumeConfiguration();
      case 'YTDPriceVolumeConfiguration':
        return new YTDPriceVolumeConfiguration();
      case 'DisclosureConfiguration':
        return new DisclosureConfiguration();
      default:
        return undefined;
    }
  }
  private static getUpdatedConfiguration(
    reportSection: ReportSectionConfigurationWithOrder
  ): ReportSectionConfigurationBase | undefined {
    const configurationInstance =
      ItineraryParticipantInfoConfigurationPanelService.getConfigurationInstanceFromConfigClass(reportSection);
    if (configurationInstance) {
      configurationInstance.availableDataSections = reportSection.config.availableDataSections.map(section => {
        // When allowUnselectAll is true and all subsections are unchecked, uncheck the parent section
        if (section.allowUnselectAll && section.subSections) {
          const allSubsectionsUnchecked = section.subSections.every(subsection => !subsection.isSelected);
          if (allSubsectionsUnchecked) {
            return { ...section, isSelected: false };
          }
        }
        return section;
      });
      configurationInstance.additionalConfiguration = reportSection.config.additionalConfiguration;
    }
    return configurationInstance;
  }

  public static GetUpdatedSizes(reportSection: ReportSectionConfigurationWithOrder): {
    minSize: number;
    maxSize: number;
  } {
    const config = ItineraryParticipantInfoConfigurationPanelService.getUpdatedConfiguration(reportSection);
    let minSize: number;
    let maxSize: number;

    try {
      minSize = config?.minSize || reportSection.config.minSize;
    } catch (e) {
      minSize = reportSection.config.minSize;
    }

    try {
      maxSize = config?.maxSize || reportSection.config.maxSize;
    } catch (e) {
      maxSize = reportSection.config.maxSize;
    }

    return {
      minSize: minSize,
      maxSize: maxSize
    };
  }
  private static UpdateDataSectionsSelection(
    dataSection: ReportSectionConfigForDraggableItem,
    checked: boolean,
    reportSection: ReportSectionConfigurationWithOrder,
    maxGridUnitsAllowed: number | undefined
  ) {
    return reportSection.config.availableDataSections.reduce(
      (acc, section: ReportSectionDataSection) => {
        const isTargetSection = section.name === dataSection.name;
        let targetSection;
        if (isTargetSection) {
          targetSection = section;
        }
        let updatedSubSection = section.subSections;
        let sectionSelected = section.isSelected;

        // If this is not a parent item, update the sub-sections
        if (!dataSection.isParentItem) {
          updatedSubSection = section.subSections?.map(subSection => {
            const isTargetSubSection = subSection.name === dataSection.name;

            // In allowUnselectAll mode, we don't need to check if it's the last item in the group
            if (section.allowUnselectAll) {
              return {
                ...subSection,
                isSelected: isTargetSubSection ? checked : subSection.isSelected
              };
            }

            // Only enforce groupName restrictions if allowUnselectAll is false AND groupName is explicitly defined
            // If groupName is undefined/not set, allow free deselection (no grouping enforced)
            if (subSection.groupName) {
              const otherGroupItems = section.subSections?.filter(
                sub => sub.isSelected && sub.groupName === subSection.groupName && sub.name !== subSection.name
              );
              const isLastSelected = otherGroupItems && otherGroupItems.length === 0 && subSection.isSelected;

              return {
                ...subSection,
                isSelected: isTargetSubSection ? (isLastSelected ? true : checked) : subSection.isSelected
              };
            }

            // No groupName defined - allow free deselection
            return {
              ...subSection,
              isSelected: isTargetSubSection ? checked : subSection.isSelected
            };
          }); // If all subsections are unchecked and allowUnselectAll is true, uncheck the parent section
          if (section.allowUnselectAll) {
            const allSubsectionsUnchecked = updatedSubSection?.every(subsection => !subsection.isSelected);
            if (allSubsectionsUnchecked) {
              sectionSelected = false;
            }
          }
        }

        // Push the updated section to the accumulator
        acc.dataSections.push({
          ...section,
          isSelected: isTargetSection ? checked : sectionSelected,
          subSections: updatedSubSection
        });

        if (targetSection && targetSection?.unit && section.unit && maxGridUnitsAllowed) {
          acc.targetSection = targetSection;
          acc.canCheckAllDataSections = ItineraryParticipantInfoConfigurationPanelService.CanUpdateDataSectionUnit(
            targetSection,
            reportSection,
            maxGridUnitsAllowed,
            checked
          );
        }

        return acc;
      },
      {
        dataSections: [] as ReportSectionDataSection[],
        canCheckAllDataSections: true,
        targetSection: null as ReportSectionDataSection | null
      }
    );
  }

  public static DisableDataSectionsIfMaxGridUnitsExceeds(
    dataSelectionsInfo: {
      dataSections: ReportSectionDataSection[];
      canCheckAllDataSections: boolean;
      targetSection: ReportSectionDataSection | null;
    },
    maxGridUnitsAllowed: number | undefined
  ): ReportSectionDataSection[] {
    const { canCheckAllDataSections, dataSections, targetSection } = dataSelectionsInfo;

    if (maxGridUnitsAllowed && targetSection && targetSection?.unit) {
      return dataSections.map(section => {
        if (section.unit && !section.isSelected) {
          return {
            ...section,
            canConfigure: canCheckAllDataSections // disable data points which are not selected when sum exceeds maxGridUnitsAllowed
          };
        }
        return section;
      });
    }
    return dataSections;
  }

  public static UpdateReportSectionConfigOnDataSectionToggle(
    dataSection: ReportSectionConfigForDraggableItem,
    checked: boolean,
    reportSection: ReportSectionConfigurationWithOrder
  ): ReportSectionConfigurationWithOrder {
    const maxGridUnitsAllowed: number = reportSection.config.maxGridUnitsAllowed ?? 21.5;

    const isParticipantInfoSection = reportSection.config.name === 'participantsInfo';
    if (isParticipantInfoSection && checked) {
      const currentTotal = ItineraryParticipantInfoConfigurationPanelService.CalculateTotalSelectedUnits(
        reportSection.config.availableDataSections
      );
      const targetUnit = dataSection.unit || 0;

      if (currentTotal + targetUnit > 16) {
        return reportSection;
      }
    }

    const UpdateDataSectionsSelection = ItineraryParticipantInfoConfigurationPanelService.UpdateDataSectionsSelection(
      dataSection,
      checked,
      reportSection,
      maxGridUnitsAllowed
    );

    const updatedAvailableDataSections =
      ItineraryParticipantInfoConfigurationPanelService.DisableDataSectionsIfMaxGridUnitsExceeds(
        UpdateDataSectionsSelection,
        maxGridUnitsAllowed
      );

    const updatedSelectedDataSectionsCount = updatedAvailableDataSections.reduce(
      (count, section) => count + (section.isSelected ? 1 : 0),
      0
    );

    const updatedReportSection = {
      ...reportSection,
      isSelected: updatedSelectedDataSectionsCount < 1 ? false : reportSection.isSelected,
      config: {
        ...reportSection.config,
        availableDataSections: updatedAvailableDataSections,
        selectedDataSectionsCount: updatedSelectedDataSectionsCount
      } as ReportSectionConfigurationBase
    };

    const { maxSize, minSize } =
      ItineraryParticipantInfoConfigurationPanelService.GetUpdatedSizes(updatedReportSection);
    updatedReportSection.config.minSize = minSize;
    updatedReportSection.config.maxSize = maxSize;
    return updatedReportSection;
  }
}

export function getTemplateDesignerSelectedReportState(
  reportSectionConfig: ReportSectionConfigurationWithOrder | undefined
): TemplateDesignerSelectedReportState | null {
  if (reportSectionConfig) {
    return {
      reportSectionConfig: reportSectionConfig,
      reportSectionConfigDraggableItem:
        ItineraryParticipantInfoConfigurationPanelService.PrepareDraggableItemsConfig(reportSectionConfig)
    };
  }
  return null;
}
